// v4.11.0 — MINI-SITE UNIFIÉ (design "capture" émeraude/or).
// Une seule expérience mini-site pour TOUT : commerces Food/Beauté (/place/:placeId),
// commerçants marketplace, mini-sites Pro et mocks (/commercant/:slug).
// Remplace l'ancien MerchantPage (supprimé) — données transférées, design conservé.
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { openWhatsAppOrder } from "@/lib/whatsappOrder";
import { BeautyBookingBlock } from "@/components/DivertissementCard";

const API = process.env.REACT_APP_BACKEND_URL || "";
const fmtF = (n) => `${Number(n || 0).toLocaleString("fr-FR")} FCFA`;

const WA_SVG = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

function imgUrl(u) {
  if (!u) return null;
  return u.startsWith("/") ? `${API}${u}` : u;
}

// Normalise les 2 sources de données vers un modèle de vue unique.
function toViewModel({ place, merchant }) {
  if (place) {
    return {
      image: imgUrl(place.image_url),
      categoryLabel: `${place.category_emoji || ""} ${String(place.category_label || "").toUpperCase()} · ${place.commune_name || ""}`,
      accent: place.accent || "#EFC75E",
      name: place.nom,
      verified: !!place.verified,
      description: place.description,
      offer: place.platform_offer ? `${place.platform_offer} · code ${place.platform_offer_code}` : null,
      hours: place.horaires || null,
      priceFrom: Number(place.tarif_fcfa) || null,
      address: [place.adresse_civ, place.commune_name].filter(Boolean).join(" — "),
      phone: place.telephone || null,
      gps: (place.lat && place.lng) ? { lat: place.lat, lng: place.lng } : null,
      rating: null, reviewsCount: null,
      specialties: [], products: [], reviews: [],
      waSlug: null,
    };
  }
  const m = merchant;
  const products = (m.products || []).map((p) => ({
    id: p.id, name: p.name, price: Number(p.price_cfa) || null,
    image: imgUrl(p.image), desc: p.desc || null,
  }));
  const prices = products.map((p) => p.price).filter((x) => x > 0);
  const promo = Array.isArray(m.promotions) && m.promotions.length ? m.promotions[0] : null;
  return {
    image: imgUrl(m.hero_image || (m.photos && m.photos[0])),
    categoryLabel: m.subtitle || [String(m.category || m.type || "commerce").toUpperCase(), m.commune || m.location].filter(Boolean).join(" · "),
    accent: "#EFC75E",
    name: m.name,
    verified: !!(m.verified || m.is_verified),
    description: m.presentation || m.about || m.tagline || null,
    offer: promo ? `🎁 ${promo.label || promo.text || String(promo)}` : null,
    hours: (typeof m.hours_text === "string" && m.hours_text)
      || (typeof m.hours === "string" ? m.hours : null)
      || (typeof m.is_open === "boolean" ? (m.is_open ? "Ouvert actuellement" : "Fermé actuellement") : null),
    priceFrom: prices.length ? Math.min(...prices) : null,
    address: [m.address, m.commune || m.location].filter(Boolean).join(" — "),
    phone: m.phone || m.whatsapp || null,
    gps: (m.lat && m.lng) ? { lat: m.lat, lng: m.lng } : null,
    rating: m.rating || null,
    reviewsCount: m.reviews_count || null,
    specialties: m.specialties || [],
    products,
    reviews: (m.reviews || []).slice(0, 3),
    waSlug: (m.whatsapp || m.phone) ? (m.slug || m.merchant_id) : null,
  };
}

export default function MiniSitePage() {
  const { placeId, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isPlace = location.pathname.startsWith("/place/");
  const [vm, setVm] = useState(null);
  const [rawPlace, setRawPlace] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setVm(null); setRawPlace(null); setErr(null);
    if (isPlace) {
      const base = String(placeId || "").startsWith("beaute-") ? "beaute" : "food";
      fetch(`${API}/api/v3/${base}/${placeId}`)
        .then((r) => r.json())
        .then((d) => { if (d.ok) { setVm(toViewModel({ place: d.item })); setRawPlace(d.item); } else setErr("Introuvable"); })
        .catch(() => setErr("Erreur réseau"));
    } else {
      fetch(`${API}/api/v3/merchants/${encodeURIComponent(slug)}`, { credentials: "include" })
        .then((r) => r.json())
        .then((d) => { if (d.ok && d.merchant) setVm(toViewModel({ merchant: d.merchant })); else setErr("Commerçant introuvable"); })
        .catch(() => setErr("Erreur réseau"));
    }
  }, [placeId, slug, isPlace]);

  const buy = useMemo(() => (p) => {
    const q = new URLSearchParams({ buy: p.id, m: slug || "", label: p.name || "", price: String(p.price || ""), mname: vm?.name || "" });
    window.location.href = `/?${q.toString()}`;
  }, [slug, vm]);

  if (err) return <div data-testid="minisite-error" style={{ minHeight: "100vh", background: "#07130D", color: "#F4F1E8", display: "grid", placeItems: "center" }}>❌ {err}</div>;
  if (!vm) return <div data-testid="minisite-loading" style={{ minHeight: "100vh", background: "#07130D", color: "#F4F1E8", display: "grid", placeItems: "center" }}>⏳ Chargement…</div>;

  const gpsHref = vm.gps ? `/navigate?dest=${vm.gps.lat},${vm.gps.lng}&label=${encodeURIComponent(vm.name)}` : null;

  return (
    <div data-testid="minisite-page" style={{ minHeight: "100vh", background: "#07130D", color: "#F4F1E8", fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* ── HERO (design capture) ── */}
      <div style={{ position: "relative", height: 260, overflow: "hidden", background: "#0B2417" }}>
        {vm.image && <img src={vm.image} alt={vm.name} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.65)" }} />}
        <button data-testid="minisite-back" onClick={() => navigate(-1)} style={{ position: "absolute", top: 14, left: 14, width: 36, height: 36, borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "rgba(0,0,0,0.45)", color: "#fff", fontSize: 17, cursor: "pointer" }}>←</button>
        <button
          data-testid="minisite-share"
          aria-label="Partager ce mini-site"
          onClick={async () => {
            const url = window.location.href;
            const text = `${vm.name} · Mini-site AYA`;
            try { await navigator.clipboard?.writeText(url); } catch { /* copie optionnelle */ }
            if (navigator.share) {
              try { await navigator.share({ title: vm.name, text, url }); return; } catch { /* annulé → WhatsApp */ }
            }
            window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, "_blank", "noopener");
          }}
          style={{ position: "absolute", top: 14, right: 14, display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 13px", borderRadius: 10, border: "1px solid rgba(37,211,102,0.55)", background: "rgba(0,0,0,0.45)", color: "#25D366", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
        >{WA_SVG} Partager</button>
        <div style={{ position: "absolute", bottom: 14, left: 18, right: 18 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.6, color: vm.accent }}>{vm.categoryLabel}</div>
          <h1 style={{ margin: "4px 0 0", fontSize: 24, fontWeight: 800 }}>{vm.name} {vm.verified && "✅"}</h1>
          {vm.rating && (
            <div style={{ fontSize: 12, marginTop: 3, color: "#EFC75E", fontWeight: 700 }}>
              ⭐ {vm.rating}{vm.reviewsCount ? ` · ${vm.reviewsCount} avis` : ""}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "18px 16px 60px" }}>
        {vm.description && <p style={{ fontSize: 13.5, lineHeight: 1.6, opacity: 0.85 }}>{vm.description}</p>}

        {vm.offer && (
          <div data-testid="minisite-offer" style={{ padding: "10px 12px", borderRadius: 10, background: "rgba(239,199,94,0.10)", border: "1px solid rgba(239,199,94,0.4)", fontSize: 12.5, fontWeight: 700, color: "#EFC75E", marginBottom: 12 }}>
            {vm.offer}
          </div>
        )}

        <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
          {vm.hours && <div>🕐 <strong>{vm.hours}</strong></div>}
          {vm.priceFrom != null && <div>💰 À partir de <strong>{fmtF(vm.priceFrom)}</strong></div>}
          {vm.address && <div>📍 {vm.address}</div>}
          {vm.phone && <div>📞 {vm.phone}</div>}
        </div>

        {/* ── CTA (capture : GPS + Appeler, + WhatsApp si dispo) ── */}
        <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
          {gpsHref && (
            <a data-testid="minisite-gps" href={gpsHref} style={{ flex: 1, minWidth: 140, textAlign: "center", textDecoration: "none", padding: "12px", borderRadius: 999, background: "linear-gradient(135deg,#00FF88,#00CC6E)", color: "#052012", fontWeight: 800, fontSize: 13.5 }}>🚀 Y aller (GPS)</a>
          )}
          {vm.phone && (
            <a data-testid="minisite-call" href={`tel:${String(vm.phone).replace(/\s/g, "")}`} style={{ flex: 1, minWidth: 140, textAlign: "center", textDecoration: "none", padding: "12px", borderRadius: 999, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.2)", color: "#F4F1E8", fontWeight: 800, fontSize: 13.5 }}>📞 Appeler</a>
          )}
          {vm.waSlug && (
            <button
              data-testid="minisite-whatsapp"
              onClick={() => openWhatsAppOrder({ merchantSlug: vm.waSlug })}
              style={{ flex: 1, minWidth: 140, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "12px", borderRadius: 999, cursor: "pointer", background: "rgba(37,211,102,0.12)", border: "1px solid rgba(37,211,102,0.55)", color: "#25D366", fontWeight: 800, fontSize: 13.5 }}
            >{WA_SVG} WhatsApp</button>
          )}
        </div>

        {/* ── PRISE DE RDV Beauté (jour + créneau, même flux que le chat) ── */}
        {rawPlace && String(rawPlace.id || "").startsWith("beaute-") && (
          <div style={{ marginTop: 18 }} data-testid="minisite-booking">
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 6px", color: "#EFC75E" }}>📅 Réserver un créneau</h2>
            <BeautyBookingBlock item={rawPlace} accent={rawPlace.accent || "#EFC75E"} />
          </div>
        )}

        {/* ── SPÉCIALITÉS ── */}
        {vm.specialties.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 20 }} data-testid="minisite-specialties">
            {vm.specialties.map((s, i) => (
              <span key={i} style={{ fontSize: 11.5, fontWeight: 700, padding: "5px 11px", borderRadius: 999, background: "rgba(239,199,94,0.08)", border: "1px solid rgba(239,199,94,0.3)", color: "#EFC75E" }}>{s}</span>
            ))}
          </div>
        )}

        {/* ── PRODUITS (données transférées de l'ancien mini-site) ── */}
        {vm.products.length > 0 && (
          <div style={{ marginTop: 26 }} data-testid="minisite-products">
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 12px", color: "#EFC75E" }}>Produits ({vm.products.length})</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
              {vm.products.map((p) => (
                <article key={p.id} data-testid={`minisite-product-${p.id}`} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(239,199,94,0.18)", borderRadius: 14, overflow: "hidden" }}>
                  {p.image && (
                    <div style={{ height: 120, overflow: "hidden", background: "#0B2417" }}>
                      <img src={p.image} alt={p.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  )}
                  <div style={{ padding: "10px 12px 12px" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800 }}>{p.name}</div>
                    {p.desc && <div style={{ fontSize: 11.5, opacity: 0.65, marginTop: 3, lineHeight: 1.45 }}>{p.desc}</div>}
                    {p.price != null && <div style={{ fontSize: 13, fontWeight: 800, color: "#EFC75E", marginTop: 6 }}>{fmtF(p.price)}</div>}
                    <div style={{ display: "flex", gap: 6, marginTop: 9 }}>
                      <button
                        data-testid={`minisite-product-buy-${p.id}`}
                        onClick={() => buy(p)}
                        style={{ flex: 1, padding: "8px 6px", borderRadius: 9, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#00FF88,#00CC6E)", color: "#052012", fontWeight: 800, fontSize: 11.5 }}
                      >+ Wallet</button>
                      {vm.waSlug && (
                        <button
                          data-testid={`minisite-product-whatsapp-${p.id}`}
                          aria-label="Commander sur WhatsApp"
                          onClick={() => openWhatsAppOrder({ merchantSlug: vm.waSlug, productId: p.id, productName: p.name, priceFcfa: p.price })}
                          style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "8px 4px", borderRadius: 9, cursor: "pointer", whiteSpace: "nowrap", background: "rgba(37,211,102,0.12)", border: "1px solid rgba(37,211,102,0.5)", color: "#25D366", fontWeight: 800, fontSize: 10.5 }}
                        >{WA_SVG} WhatsApp</button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* ── AVIS ── */}
        {vm.reviews.length > 0 && (
          <div style={{ marginTop: 26 }} data-testid="minisite-reviews">
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 12px", color: "#EFC75E" }}>Avis clients</h2>
            <div style={{ display: "grid", gap: 9 }}>
              {vm.reviews.map((r, i) => (
                <div key={r.id || i} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "10px 13px" }}>
                  <div style={{ fontSize: 12, fontWeight: 800 }}>{"⭐".repeat(Math.round(r.rating || 5))} <span style={{ opacity: 0.7, fontWeight: 600 }}>· {r.author}</span></div>
                  <div style={{ fontSize: 12.5, opacity: 0.8, marginTop: 4, lineHeight: 1.5 }}>{r.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 26, fontSize: 11, opacity: 0.45, textAlign: "center" }}>Mini-site propulsé par AYA 💛</div>
      </div>
    </div>
  );
}
